<script setup lang="ts">
import moment from "moment-timezone";

defineOptions({ name: "CashBookModal" });

const emit = defineEmits<{ (e: "saved"): void }>();

const { t } = useI18n();
const { $api } = useNuxtApp();
const toast = useToast();

interface CashBook {
  id?: string;
  name: string;
  currency: string;
  openingBalance: number;
  openingMonth: string;
}

const isOpen = ref(false);
const saveLoading = ref(false);
const books = ref<CashBook[]>([]);

// openingMonth is the anchor the balance walk starts from and stays a
// "2026-09" string — GetFinancialAnalytics projects it straight into a month
// key and compares it as text. The picker speaks unix seconds, so it is handed
// the first of that month and its answer is folded back to a month. Bound
// through :model-value rather than v-model because each row needs its own
// conversion and a computed cannot be made per array item.
const monthToUnix = (month: string) =>
  month ? moment(month, "YYYY-MM").startOf("month").unix() : 0;
const unixToMonth = (value: number) => (value ? moment.unix(value).format("YYYY-MM") : "");

async function open() {
  const response = await $api<{ items: CashBook[] }>("/cash-books");
  const existing = response.items ?? [];
  books.value = ["USD", "VND"].map(
    (currency) =>
      existing.find((b) => b.currency === currency) ?? {
        name: "",
        currency,
        openingBalance: 0,
        openingMonth: moment().format("YYYY-MM"),
      },
  );
  isOpen.value = true;
}

async function submit() {
  saveLoading.value = true;
  try {
    for (const book of books.value) {
      if (!book.name) continue;
      await $api("/cash-books", { method: "PUT", body: book });
    }
    toast.success({ title: t("message.updateSuccess") });
    isOpen.value = false;
    emit("saved");
  } catch {
    toast.error({ title: t("message.updateFailed") });
  } finally {
    saveLoading.value = false;
  }
}

defineExpose({ open });
</script>

<template>
  <Modal
    v-model:open="isOpen"
    :title="t('finance.cashBooks')"
    :description="t('finance.cashBookHelp')"
  >
    <div class="flex flex-col gap-large">
      <div v-for="book in books" :key="book.currency" class="flex flex-col gap-medium">
        <h3 class="text-body-primary">{{ book.currency }}</h3>
        <FormField v-model="book.name" :label="t('finance.cashBookName')" />
        <div class="flex flex-col gap-xsmall md:flex-row md:gap-small">
          <FormNumber
            v-model="book.openingBalance"
            :label="t('finance.openingBalance')"
            class="w-full md:w-1/2"
          />
          <FormDatePicker
            :model-value="monthToUnix(book.openingMonth)"
            format="MM/YYYY"
            :label="t('finance.openingMonth')"
            class="w-full md:w-1/2"
            @update:model-value="(value: number) => (book.openingMonth = unixToMonth(value))"
          />
        </div>
        <Divider />
      </div>
    </div>

    <template #actions>
      <Button variant="tertiary" size="small" @click="isOpen = false">
        {{ t("action.cancel") }}
      </Button>
      <Button variant="primary" size="small" :disabled="saveLoading" @click="submit">
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>
